import { useCallback, useEffect, useState } from "react";
import * as Y from "yjs";
import { useAnmeldung } from "../zustand/Anmeldung";
import { Abschnitt } from "../bausteine/Grundelemente";
import { Dokument, Folie, Gitter, Kreuz, PfeilRechts, Warnung } from "../bausteine/Symbole";
import {
  ausBase64,
  holeDokument,
  holeDokumente,
  holeSchritte,
  legeDokumentAn,
  loescheDokument,
  setzeStand,
} from "../dienst/server";
import { vorWieLange } from "../hilfen/datum";

/*
  Office — was die Fraktion selbst schreibt.

  ---------------------------------------------------------------
  Warum die Dokumente hier stehen und nicht bei den Dateien
  ---------------------------------------------------------------

  Eine hochgeladene Datei ist ein Gegenstand: Man legt sie ab und sucht sie
  an einem Ort wieder. Ein Dokument ist Arbeit: Man schreibt daran weiter,
  oft zu mehreren, oft über Wochen — und sucht es nicht an einem Ort, sondern
  erinnert sich, WANN man zuletzt daran war.

  Genau deshalb zeigt auch Word beim Öffnen keine Ordner, sondern „Zuletzt
  verwendet". Danach ist diese Ansicht gebaut: oben zum Anlegen, darunter
  nach letzter Änderung.

  Hochgeladene Word-Dateien stehen bewusst NICHT in dieser Liste. Man kann
  sie nur herunterladen, nicht öffnen — und zwei Sorten Zeilen, von denen
  die eine zum Weiterschreiben einlädt und die andere etwas herunterlädt,
  sind eine bekannte Stolperstelle.
*/

/*
  Was sich anlegen lässt. Tabelle und Präsentation stehen hier, sind aber
  abgeblendet und tragen das Wort „geplant“ — nach der Regel des Steckbriefs:
  entweder voll funktionsfähig oder erkennbar ein Ausblick, nichts dazwischen.
  Ein Feld, das fertig aussieht und beim Antippen nichts tut, zerstört im
  Vorführtermin mehr Vertrauen als eines, das ehrlich fehlt.
*/
/* Wie ein frisch angelegtes Ding heißt, bevor jemand es benennt. */
const TITEL_FUER = {
  dokument: "Neues Dokument",
  tabelle: "Neue Tabelle",
  praesentation: "Neue Präsentation",
};

const ARTEN = [
  {
    schluessel: "dokument",
    titel: "Dokument",
    kurz: "Antrag, Anfrage, Stellungnahme, Pressemitteilung",
    symbol: Dokument,
    gebaut: true,
  },
  {
    schluessel: "tabelle",
    titel: "Tabelle",
    kurz: "Haushaltsanträge, Listen, Zusammenstellungen",
    symbol: Gitter,
    gebaut: true,
  },
  {
    schluessel: "praesentation",
    titel: "Präsentation",
    kurz: "Folien für Bürgerversammlung oder Klausur",
    symbol: Folie,
    gebaut: true,
  },
];

export default function Office({ oeffne }) {
  const { token, konto, fraktion } = useAnmeldung();

  const [dokumente, setzeDokumente] = useState([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState("");
  const [legtAn, setzeLegtAn] = useState(false);

  const laden = useCallback(async () => {
    setzeFehler("");
    try {
      /* Der Server sortiert nach `-zuletzt_geschrieben` — zuletzt Bearbeitetes oben. */
      setzeDokumente(await holeDokumente(token));
    } catch (f) {
      setzeFehler(f.message);
    } finally {
      setzeLaedt(false);
    }
  }, [token]);

  useEffect(() => {
    laden();
  }, [laden]);

  /*
    Muster stehen oben als Kachel UND unten in der Liste. Das ist Absicht:
    Ein Muster ist ein Dokument — man muss es öffnen, ändern und löschen
    können wie jedes andere. Die Kachel ist nur der kurze Weg, ein neues
    daraus anzufangen.
  */
  const muster = dokumente.filter((d) => d.muster);

  /*
    Nach dem Anlegen geht das Dokument gleich auf. Wer „Neues Dokument“
    drückt, will schreiben — nicht eine Zeile mehr in einer Liste sehen.
  */
  /* Wohin ein Dokument führt, entscheidet seine Art. */
  function ansichtFuer(art) {
    if (art === "praesentation") return "praesentation";
    if (art === "tabelle") return "tabelle";
    return "textdokument";
  }

  async function neuesDokument(muster, art = "dokument") {
    setzeFehler("");
    setzeLegtAn(true);
    try {
      const satz = await legeDokumentAn(token, {
        fraktionId: konto.fraktion,
        kontoId: konto.id,
        titel: muster ? muster.titel : TITEL_FUER[art] || "Neues Dokument",
        art: muster ? muster.art || "dokument" : art,
      });

      /*
        Aus einem Muster: dessen Inhalt in das frische Dokument übernehmen.

        Kopiert wird der zusammengefasste Stand, nicht die einzelnen
        Schritte — das neue Dokument fängt damit bei null an und schleppt
        nicht die Änderungsgeschichte des Musters mit.
      */
      if (muster) {
        const stand = await standVonMuster(token, muster.id);
        if (stand) await setzeStand(token, satz.id, stand);
      }

      oeffne(ansichtFuer(satz.art), { dokumentId: satz.id });
    } catch (f) {
      setzeFehler(f.message);
    } finally {
      setzeLegtAn(false);
    }
  }

  async function entfernen(satz) {
    if (
      !window.confirm(
        `„${satz.titel}" wirklich löschen? Der Text ist damit für die ganze Fraktion weg.`
      )
    )
      return;
    try {
      await loescheDokument(token, satz.id);
      await laden();
    } catch (f) {
      setzeFehler(f.message);
    }
  }

  if (!konto.fraktion) {
    return (
      <div className="karte" style={{ marginTop: "var(--luft-5)" }}>
        <p className="tor__fehler">
          <Warnung groesse={18} />
          <span>
            Dein Konto ist keiner Fraktion zugeordnet. Deshalb siehst du hier nichts. Melde
            dich bei uns, dann richten wir das ein.
          </span>
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="detailkopf">
        <p className="detailkopf__vorspann">
          {fraktion ? `${fraktion.name} · ${fraktion.kommune}` : "Fraktion"}
        </p>
        <h1 className="detailkopf__titel">Office</h1>
        <p className="leise" style={{ marginTop: "var(--luft-2)" }}>
          Was die Fraktion selbst schreibt. Alle Mitglieder deiner Fraktion sehen es und
          können mitschreiben — sonst niemand.
        </p>
      </div>

      <div className="neuwahl">
        {ARTEN.map((art) => (
          <button
            key={art.schluessel}
            className={`neuwahl__feld ${art.gebaut ? "" : "neuwahl__feld--geplant"}`}
            onClick={art.gebaut ? () => neuesDokument(null, art.schluessel) : undefined}
            disabled={!art.gebaut || legtAn}
            aria-disabled={!art.gebaut}
          >
            <span className="neuwahl__symbol">
              <art.symbol groesse={26} />
            </span>
            <span className="neuwahl__titel">
              {art.gebaut && legtAn ? "Wird angelegt …" : art.titel}
              {!art.gebaut && <span className="marke marke--geplant">geplant</span>}
            </span>
            <span className="neuwahl__kurz">{art.kurz}</span>
          </button>
        ))}

        {/*
          Die Muster der Fraktion — Briefkopf, Musterantrag. Sie stehen
          neben „Dokument", weil sie dasselbe tun: ein neues Dokument
          anfangen, nur nicht bei null.
        */}
        {muster.map((satz) => {
          /*
            Ein Muster trägt Symbol und Wortlaut SEINER Art. Vorher stand an
            jedem „Dokument", auch an einem Tabellenmuster — wer aus einem
            Haushaltsgerüst eine neue Tabelle anfangen wollte, las dort
            „Neues Dokument aus diesem Muster" und musste raten.
          */
          const art = ARTEN.find((a) => a.schluessel === (satz.art || "dokument")) || ARTEN[0];
          return (
            <button
              key={satz.id}
              className="neuwahl__feld neuwahl__feld--muster"
              onClick={() => neuesDokument(satz)}
              disabled={legtAn}
            >
              <span className="neuwahl__symbol">
                <art.symbol groesse={26} />
              </span>
              <span className="neuwahl__titel">
                {satz.titel}
                <span className="marke">Muster</span>
              </span>
              <span className="neuwahl__kurz">
                {TITEL_FUER[art.schluessel] || "Neues Dokument"} aus diesem Muster
              </span>
            </button>
          );
        })}
      </div>

      {fehler && (
        <p className="tor__fehler" style={{ marginTop: "var(--luft-4)" }} role="alert">
          <Warnung groesse={18} />
          <span>{fehler}</span>
        </p>
      )}

      <Abschnitt
        titel={laedt ? "Wird geladen …" : "Zuletzt bearbeitet"}
        hinweis={
          !laedt && dokumente.length === 0
            ? "Noch nichts geschrieben. Der erste Antragsentwurf landet hier."
            : undefined
        }
      >
        {dokumente.map((satz) => (
          <Zeile
            key={satz.id}
            satz={satz}
            eigen={satz.konto === konto.id}
            aufOeffnen={() => oeffne(ansichtFuer(satz.art), { dokumentId: satz.id })}
            aufEntfernen={() => entfernen(satz)}
          />
        ))}
      </Abschnitt>
    </>
  );
}

function Zeile({ satz, eigen, aufOeffnen, aufEntfernen }) {
  const wer =
    satz.expand && satz.expand.konto ? satz.expand.konto.name : "Ehemaliges Mitglied";

  return (
    <div className="aufgabe">
      <span style={{ color: "var(--akzent-kraeftig)", marginTop: "0.15rem" }}>
        <Dokument groesse={20} />
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="aufgabe__titel">
          {satz.titel}
          {satz.muster && <span className="marke">Muster</span>}
        </div>
        <p className="klein leise" style={{ marginTop: "0.25rem" }}>
          {/*
            Die Zeit steht vorn, weil sie hier die Ordnung ist: Die Liste ist
            nach ihr sortiert, und danach sucht man.
          */}
          {vorWieLange(satz.zuletzt_geschrieben || satz.geaendert)} · angelegt von{" "}
          {eigen ? "dir" : wer}
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--luft-2)",
            marginTop: "var(--luft-3)",
          }}
        >
          <button className="knopf knopf--zurueckhaltend" onClick={aufOeffnen}>
            Öffnen
            <PfeilRechts groesse={16} />
          </button>
          {eigen && (
            <button className="knopf knopf--schlicht" onClick={aufEntfernen}>
              <Kreuz groesse={16} />
              Löschen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/*
  Den vollständigen Stand eines Musters zusammenbauen.

  Der verdichtete Stand allein reicht nicht: Was seit dem letzten Verdichten
  geschrieben wurde, liegt noch als einzelne Schritte daneben. Wer nur
  `stand` kopierte, bekäme ein Muster von gestern — und niemand wüsste,
  warum die letzte Änderung fehlt.
*/
async function standVonMuster(token, musterId) {
  const quelle = await holeDokument(token, musterId);
  const doc = new Y.Doc();

  if (quelle.stand) Y.applyUpdate(doc, ausBase64(quelle.stand), "muster");

  const schritte = await holeSchritte(token, musterId, quelle.stand_bis);
  for (const schritt of schritte) {
    try {
      Y.applyUpdate(doc, ausBase64(schritt.daten), "muster");
    } catch {
      /* Ein unlesbarer Schritt darf das Muster nicht unbrauchbar machen. */
    }
  }

  return Y.encodeStateAsUpdate(doc);
}
